import { calculateDefaultRecurrenceBuffer, calendarRecurrenceIntervalString } from '@frontend/api-client';
import type { CalendarRecurrence } from '@frontend/api-types';
import { dayjs } from '@frontend/duck-tape';
import type { TaskCreationOptions } from '@frontend/react-domain/hooks/useGlobalTaskCreation/helpers';
import { CardButton, Clickable, Icon, RadioInput, Spinner, Text, XStack, YStack } from '@frontend/web-react';
import { safeCss } from '@frontend/web-utils';
import type { ExtraDataType } from '@frontend/web/routes/app/calendar-recurrence';
import type { TextareaProps } from '@mantine/core';
import { Textarea } from '@mantine/core';
import type { Dispatch, SetStateAction } from 'react';

const textareaInputClassNames: TextareaProps['classNames'] = {
  input: safeCss(
    'px-md py-sm text-p-md outline-none text-textPrimary placeholder:text-textTertiary border-borderPrimary rounded-md',
  ),
  root: safeCss('gap-y-xs flex flex-col flex-1 w-full pr-md pl-xl'),
};

type CalendarRecurrenceCardProps = {
  calendarRecurrence: CalendarRecurrence;
  extraData: ExtraDataType;
  handleProcessCalendarRecurrence: (
    calendarRecurrence: CalendarRecurrence,
    dismiss: boolean,
    created?: boolean,
  ) => void;
  handleSelectCalendarRecurrence: (index: number) => void;
  index: number;
  openCreateTaskModal: (options: TaskCreationOptions) => void;
  setExtraData: Dispatch<SetStateAction<ExtraDataType>>;
};

export const CalendarRecurrenceCard = ({
  calendarRecurrence,
  extraData,
  handleProcessCalendarRecurrence,
  handleSelectCalendarRecurrence,
  index,
  openCreateTaskModal,
  setExtraData,
}: CalendarRecurrenceCardProps) => {
  const { description, nextStartDate, title } = calendarRecurrence;

  const handleCreate = () => {
    const taskDescription = extraData.description === 'Other' ? extraData.otherText : extraData.description;
    openCreateTaskModal({
      initialData: {
        buffer: calculateDefaultRecurrenceBuffer(calendarRecurrence.interval, calendarRecurrence.intervalCount),
        calendarRecurrenceId: calendarRecurrence.id,
        calendarRecurrenceTaskSuggestionIdx: extraData.taskSuggestions.indexOf(taskDescription || ''),
        description: taskDescription,
        interval: calendarRecurrence.interval,
        intervalCount: calendarRecurrence.intervalCount,
        name: taskDescription,
        nextStartDate: calendarRecurrence.nextStartDate,
      },
      isRecurrence: true,
      onCreateCallback: () => {
        handleProcessCalendarRecurrence(calendarRecurrence, false, true);
      },
      stepsToSkip: ['description', 'recurrenceSettings'],
    });
  };

  return (
    <Clickable
      className="flex h-auto flex-col border-borderPrimary rounded-lg border bg-trueWhite"
      onClick={() => handleSelectCalendarRecurrence(index)}
    >
      <YStack className="p-md gap-lg">
        <YStack className="gap-xs">
          <Text className="line-clamp-1" type="h5">
            {title}
          </Text>
          {extraData.selectedIndex === index ? (
            <Text className="line-clamp-2" type="p-sm">
              {description}
            </Text>
          ) : null}
        </YStack>
        {extraData.selectedIndex === index ? null : (
          <YStack className="gap-xs">
            <XStack className="gap-sm items-center">
              <Icon color="secondary" name="IconCalendar" />
              <Text className="text-textSecondary" type="p-sm">
                {dayjs(nextStartDate).format('ddd, MMM d')}
              </Text>
            </XStack>
            <XStack className="gap-sm items-center">
              <Icon color="secondary" name="IconRepeat" />
              <Text className="text-textSecondary" type="p-sm">
                {calendarRecurrenceIntervalString(calendarRecurrence)}
              </Text>
            </XStack>
          </YStack>
        )}
        {extraData.selectedIndex === index ? (
          <YStack onClick={(event: React.MouseEvent) => event.stopPropagation()}>
            <XStack className="flex flex-1 items-center justify-between">
              <Text type="p-sm">What would you like us to do on this cadence?</Text>
              {extraData.isStreaming ? <Spinner size={16} /> : null}
            </XStack>
            <YStack className="w-full">
              <RadioInput
                onChange={(value) => setExtraData((prev) => ({ ...prev, description: value || '' }))}
                options={extraData.taskSuggestions.map((s) => ({
                  id: s,
                  label: s,
                  value: s,
                }))}
                value={extraData.description}
              />
            </YStack>
            {extraData.description === 'Other' ? (
              <Textarea
                autoFocus
                autosize
                classNames={textareaInputClassNames}
                maxRows={10}
                minRows={1}
                name="message"
                onChange={(event) => setExtraData((prev) => ({ ...prev, otherText: event.target.value }))}
                placeholder="What do you want done?"
                value={extraData.otherText}
              />
            ) : null}
          </YStack>
        ) : null}
        <YStack>
          <XStack className="gap-sm items-end justify-end">
            {extraData.selectedIndex === index ? (
              <CardButton
                disabled={!extraData.description || (extraData.description === 'Other' && !extraData.otherText)}
                label="Create scheduled task"
                onClick={handleCreate}
                variant="filled"
              />
            ) : (
              <>
                <CardButton
                  className="bg-neutral-50"
                  label="Dismiss"
                  onClick={() => handleProcessCalendarRecurrence(calendarRecurrence, true)}
                  variant="subtle"
                />
                <CardButton
                  label="Generate suggestions"
                  onClick={() => handleSelectCalendarRecurrence(index)}
                  variant="outlined"
                />
              </>
            )}
          </XStack>
        </YStack>
      </YStack>
    </Clickable>
  );
};
